import { useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";

export function AssetPicker({ source, onStarted, disabled }) {
  const [assets, setAssets] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  const [n, setN] = useState(100);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/data-sources/${source.id}/assets`); setAssets(data); if (!data.length) toast.info("No scannable assets were found with this identity's permissions."); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const groups = useMemo(() => {
    const g = {};
    (assets || []).filter((a) => a.path.toLowerCase().includes(q.toLowerCase())).forEach((a) => { (g[a.group] = g[a.group] || []).push(a); });
    return g;
  }, [assets, q]);
  const toggle = (paths, on) => setSel((s) => { const x = new Set(s); paths.forEach((p) => (on ? x.add(p) : x.delete(p))); return x; });
  const start = async () => {
    try { const { data } = await api.post(`/data-sources/${source.id}/scans`, { assets: [...sel], sample_size: Number(n) }); toast.success("Scan started"); onStarted(data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="asset-picker">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={load} disabled={busy || disabled} data-testid="load-assets-button"><RefreshCw className={`mr-1.5 h-4 w-4 ${busy ? "animate-spin" : ""}`} />{assets ? "Refresh" : "Load"} {source.asset_label || "assets"}</Button>
        {assets && <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" className="w-56 bg-white" data-testid="asset-filter" />}
        {assets && <span className="text-xs text-slate-500">{sel.size} of {assets.length} selected (max 200 per scan)</span>}
      </div>
      {assets && (
        <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-200 bg-white">
          {Object.entries(groups).map(([g, items]) => (
            <div key={g} className="border-b last:border-0">
              <label className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
                <Checkbox checked={items.every((a) => sel.has(a.path))} onCheckedChange={(v) => toggle(items.map((a) => a.path), !!v)} data-testid={`asset-group-${g}`} />{g} <span className="font-normal text-slate-400">({items.length})</span>
              </label>
              {items.map((a) => (
                <label key={a.path} className="flex items-center gap-2 px-6 py-1 text-sm hover:bg-slate-50">
                  <Checkbox checked={sel.has(a.path)} onCheckedChange={(v) => toggle([a.path], !!v)} data-testid={`asset-${a.path}`} />
                  <span className="font-mono text-xs text-slate-700">{a.path}</span><span className="text-[10px] uppercase text-slate-400">{a.kind}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
      {assets && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3">
          <label className="flex items-center gap-2 text-xs text-slate-700">Rows sampled per asset<Input type="number" min={10} max={1000} value={n} onChange={(e) => setN(e.target.value)} className="h-8 w-24 bg-white" data-testid="sample-size-input" /></label>
          <span className="text-[11px] text-slate-500">Sampled values are classified in memory and discarded immediately.</span>
          <Button onClick={start} disabled={!sel.size || disabled} className="ml-auto bg-blue-600 hover:bg-blue-700" data-testid="start-scan-button"><Play className="mr-1.5 h-4 w-4" />Scan {sel.size || ""} selected</Button>
        </div>
      )}
    </div>
  );
}
